/**
 * Generic policy + context normalization for subscription-based APMs.
 *
 * Backend controls global enablement and sends PM config with capability flags
 * (`subscription_based_apm`, `set_line_items`). This module consumes those flags and
 * derives what cb-checkout is allowed to do at runtime, based on surface
 * (checkout | portal | paynow) and current page state (subscriptions, invoices, cart).
 *
 * This module is intentionally gateway-agnostic. Stripe Klarna is the first consumer
 * of this infrastructure; any Stripe/Klarna-specific rules live in
 * `stripe-klarna-subscription-apm-policy.js` and must not be added here.
 *
 * Nothing in this file changes whether Klarna is configured for the site —
 * that is decided by the backend. This file only answers:
 * "given that the PM is configured, is this page context valid for it?"
 */
function asArray(value) {
  if (value == null) {
    return [];
  }
  return Array.isArray(value) ? value : [];
}

export function isSubscriptionBasedApm(pm) {
  return !!(pm && pm.subscription_based_apm === true);
}

export function hasSetLineItems(pm) {
  return !!(pm && pm.set_line_items === true);
}

function pickSubscriptionReference(ctx) {
  const fromCtx =
    ctx.subscription_handle ??
    ctx.subHandle ??
    ctx.sub_handle ??
    ctx.subscriptionHandle ??
    ctx.subscription_id ??
    ctx.subscriptionId;
  if (fromCtx != null && fromCtx !== '') {
    return String(fromCtx);
  }
  const routeQuery = ctx.route && ctx.route.query;
  if (routeQuery && routeQuery.sub_handle != null && routeQuery.sub_handle !== '') {
    return String(routeQuery.sub_handle);
  }
  const hosted = ctx.hostedPage || ctx.subscription_data;
  const fromHosted = hosted && (hosted.subscription_handle ?? hosted.subscriptionHandle);
  if (fromHosted != null && fromHosted !== '') {
    return String(fromHosted);
  }
  return null;
}

function subscriptionsFromContext(ctx) {
  return asArray(
    ctx.subscriptions ??
      ctx.subscription_list ??
      (ctx.customer && ctx.customer.subscriptions)
  );
}

function selectedInvoiceRowsFromContext(ctx) {
  return asArray(ctx.selected_invoices ?? ctx.invoices ?? ctx.selectedInvoices);
}

function distinctInvoiceSubscriptionIds(ctx) {
  const rows = selectedInvoiceRowsFromContext(ctx);
  const ids = new Set();
  rows.forEach((row) => {
    const id = row && (row.subscription_id ?? row.subscriptionId);
    if (id != null && id !== '') {
      ids.add(String(id));
    }
  });
  return Array.from(ids);
}

function lineItemsFromContext(ctx) {
  const holder = ctx.order_estimate_holder;
  const imm = holder && holder.immediateEstimate;
  const buckets = [
    ctx.line_items,
    ctx.lineItems,
    ctx.estimate && ctx.estimate.line_items,
    ctx.estimate && ctx.estimate.lineItems,
    holder && holder.estimate && holder.estimate.line_items,
    holder && holder.estimate && holder.estimate.lineItems,
    imm && imm.line_items,
    imm && imm.lineItems
  ];
  for (let i = 0; i < buckets.length; i++) {
    const arr = asArray(buckets[i]);
    if (arr.length > 0) {
      return arr;
    }
  }
  return [];
}

function refInSubscriptions(ref, subscriptions) {
  return !!(ref && subscriptions.some((s) => s && String(s.id) === ref));
}

/**
 * Hosted checkout: unambiguous "new subscription" purchase when the cart has a
 * recurring plan schedule and the customer has no preloaded subscriptions yet.
 */
function isCheckoutNewSubscriptionCreationContext(ctx) {
  if (!ctx || ctx.hosted_page_type !== 'checkout') {
    return false;
  }
  const subs = subscriptionsFromContext(ctx);
  if (subs.length > 1) {
    return false;
  }
  if (distinctInvoiceSubscriptionIds(ctx).length > 1) {
    return false;
  }
  const ref = pickSubscriptionReference(ctx);
  if (ref && subs.length === 0) {
    return false;
  }
  if (ref && subs.length === 1 && !refInSubscriptions(ref, subs)) {
    return false;
  }
  if (subs.length > 0) {
    return false;
  }
  const d = ctx.cart_plan && ctx.cart_plan.details;
  if (!d || d.period_unit == null || d.period == null) {
    return false;
  }
  return true;
}

/**
 * When {@code cart_plan} is not yet on the runtime snapshot, infer recurring
 * purchase from checkout line items (same sources as {@link #lineItemsFromContext}:
 * {@code estimate}, {@code order_estimate_holder}, etc.).
 */
function isCheckoutRecurringFromEstimateLineItems(ctx) {
  if (!ctx) {
    return false;
  }
  const lines = lineItemsFromContext(ctx);
  return lines.some((line) => {
    if (!line) {
      return false;
    }
    const et = String(line.entity_type || line.item_type || '').toLowerCase();
    if (
      et !== 'plan' &&
      et !== 'plan_item_price' &&
      et !== 'plan_setup' &&
      et !== 'charge_item_price'
    ) {
      return false;
    }
    const d = line.details || line;
    return d != null && d.period_unit != null && d.period != null;
  });
}

/**
 * Checkout with a recurring plan in cart (add/change subscription), including
 * existing customers with preloaded subscriptions (they do not get
 * {@link #isCheckoutNewSubscriptionCreationContext}).
 * Used to allow saved + new sub-APM PM and subscription payload on checkout.
 */
function isCheckoutRecurringSubPurchaseInCartFromRuntime(ctx) {
  if (!ctx || ctx.hosted_page_type !== 'checkout') {
    return false;
  }
  if (distinctInvoiceSubscriptionIds(ctx).length > 1) {
    return false;
  }
  const d = ctx.cart_plan && ctx.cart_plan.details;
  if (d && d.period_unit != null && d.period != null) {
    return true;
  }
  return isCheckoutRecurringFromEstimateLineItems(ctx);
}

function isSubscriptionApmContextForNewPm(context) {
  if (!context) {
    return false;
  }
  if (context.isCheckoutRecurringSubPurchaseInCart) {
    return true;
  }
  if (context.isSingleSubscriptionCreationContext) {
    return true;
  }
  return !!(context.hasSubscriptionContext && context.isSingleSubscriptionContext);
}

/**
 * Checkout: existing customer profile already has multiple subscriptions, cart is adding another
 * (no {@code subscription_handle} / reference yet). {@link #isSubscriptionApmContextForNewPm} can be false
 * when recurring was not inferred from {@code cart_plan} alone — still allow saved PM so payment-intent
 * collect can run for the new subscription-scoped authorization.
 */
function checkoutAllowSavedSubApmReturningCustomerMultiSubNewPurchase(context) {
  if (!context || context.surface !== 'checkout') {
    return false;
  }
  if (distinctInvoiceSubscriptionIds(context).length > 1) {
    return false;
  }
  const ref = context.subscriptionReference || pickSubscriptionReference(context);
  if (ref != null && ref !== '') {
    return false;
  }
  if (!context.hasSubscriptionContext || context.isSingleSubscriptionContext) {
    return false;
  }
  const lines = lineItemsFromContext(context);
  return lines.length > 0;
}

function getSurfaceFromRuntimeContext(ctx) {
  const hp = ctx?.hosted_page_type;
  if (hp === 'checkout' || hp === 'portal' || hp === 'paynow') {
    return hp;
  }
  return null;
}

/**
 * Normalize checkout / portal / paynow subscription snapshots into one canonical shape.
 *
 * Surface differences matter for policy:
 * - checkout: subscription context comes from cart plan + estimate + subscription_handle.
 * - portal: an existing subscription is always in scope; adding a new sub-APM PM is blocked.
 * - paynow: context is driven entirely by selected invoices — single vs multi subscription
 *   determines whether a saved sub-APM can be reused.
 *
 * Does not mutate the input object.
 */
export function deriveSubscriptionContext(runtimeContext) {
  const ctx =
    runtimeContext != null && typeof runtimeContext === 'object' ? runtimeContext : {};

  const subscriptions = subscriptionsFromContext(ctx).slice();
  const lineItems = lineItemsFromContext(ctx).slice();
  const invoiceSubIds = distinctInvoiceSubscriptionIds(ctx);
  const ref = pickSubscriptionReference(ctx);

  let hasSubscriptionContext = false;
  let isSingleSubscriptionContext = false;
  let subscriptionCount = 0;
  let subscriptionReference = null;

  if (invoiceSubIds.length > 1) {
    hasSubscriptionContext = true;
    isSingleSubscriptionContext = false;
    subscriptionCount = invoiceSubIds.length;
    subscriptionReference = null;
  } else if (subscriptions.length > 0 && ref) {
    const inList = refInSubscriptions(ref, subscriptions);
    const invoiceOk =
      invoiceSubIds.length === 0 ||
      (invoiceSubIds.length === 1 && invoiceSubIds[0] === ref);
    if (inList && invoiceOk) {
      hasSubscriptionContext = true;
      isSingleSubscriptionContext = true;
      subscriptionCount = 1;
      subscriptionReference = ref;
    } else {
      hasSubscriptionContext = false;
      isSingleSubscriptionContext = false;
      subscriptionCount = subscriptions.length;
      subscriptionReference = null;
    }
  } else if (subscriptions.length > 0 && !ref) {
    if (subscriptions.length === 1) {
      const onlyId =
        subscriptions[0] && subscriptions[0].id != null ? String(subscriptions[0].id) : null;
      const invoiceOk =
        invoiceSubIds.length === 0 ||
        (invoiceSubIds.length === 1 && onlyId && invoiceSubIds[0] === onlyId);
      hasSubscriptionContext = invoiceOk && !!onlyId;
      isSingleSubscriptionContext = hasSubscriptionContext;
      subscriptionCount = subscriptions.length;
      subscriptionReference = onlyId;
    } else {
      hasSubscriptionContext = true;
      isSingleSubscriptionContext = false;
      subscriptionCount = subscriptions.length;
      subscriptionReference = null;
    }
  } else if (subscriptions.length === 0 && invoiceSubIds.length === 1) {
    hasSubscriptionContext = true;
    isSingleSubscriptionContext = true;
    subscriptionCount = 1;
    subscriptionReference = invoiceSubIds[0];
  } else if (subscriptions.length === 0 && ref) {
    hasSubscriptionContext = false;
    isSingleSubscriptionContext = false;
    subscriptionCount = 0;
    subscriptionReference = null;
  } else {
    hasSubscriptionContext = false;
    isSingleSubscriptionContext = false;
    subscriptionCount = 0;
    subscriptionReference = null;
  }

  const isSingleSubscriptionCreationContext = isCheckoutNewSubscriptionCreationContext(ctx);
  const isCheckoutRecurringSubPurchaseInCart = isCheckoutRecurringSubPurchaseInCartFromRuntime(ctx);
  const surface = getSurfaceFromRuntimeContext(ctx);
  const isPaynowMultiSubscriptionInvoiceSelection =
    surface === 'paynow' && invoiceSubIds.length > 1;
  const isPaynowSingleSubscriptionFromInvoices =
    surface === 'paynow' && invoiceSubIds.length === 1;
  const invoiceRows = selectedInvoiceRowsFromContext(ctx);
  const paynowSelectedInvoiceCount = surface === 'paynow' ? invoiceRows.length : 0;
  const firstInv = invoiceRows.length === 1 ? invoiceRows[0] : null;
  const firstInvSubId =
    firstInv && (firstInv.subscription_id ?? firstInv.subscriptionId) != null
      ? String(firstInv.subscription_id ?? firstInv.subscriptionId)
      : null;
  const isPaynowSingleInvoiceSingleSubscription =
    surface === 'paynow' &&
    invoiceRows.length === 1 &&
    invoiceSubIds.length === 1 &&
    firstInvSubId != null &&
    firstInvSubId !== '' &&
    invoiceSubIds[0] === firstInvSubId;

  return {
    hasSubscriptionContext,
    isSingleSubscriptionContext,
    subscriptionCount,
    subscriptionReference,
    subscriptions,
    lineItems,
    isSingleSubscriptionCreationContext,
    isCheckoutRecurringSubPurchaseInCart,
    surface,
    isPaynowMultiSubscriptionInvoiceSelection,
    isPaynowSingleSubscriptionFromInvoices,
    paynowSelectedInvoiceCount,
    isPaynowSingleInvoiceSingleSubscription
  };
}

/**
 * @returns {'default'|'never'} default = follow page/save policy; never = one-time pay, no store intent
 */
export function getSubscriptionApmSavePaymentMethodIntent(pm, context) {
  if (!isSubscriptionBasedApm(pm) || !context) {
    return 'default';
  }
  if (context.surface === 'paynow') {
    return 'never';
  }
  return 'default';
}

/**
 * Single policy object for subscription-based APM; keeps capability + runtime in one place.
 */
export function resolveSubscriptionApmPolicy(pm, context) {
  return {
    canAddPm: canAddPm({ pm, context }),
    canShowExistingPm: canShowExistingPm({ pm, context, savedPaymentSource: null }),
    shouldSendSubscriptionDetails: shouldSendSubscriptionDetails({ pm, context }),
    shouldSendLineItems: shouldSendLineItems({ pm, context }),
    savePaymentMethod: getSubscriptionApmSavePaymentMethodIntent(pm, context)
  };
}

function subscriptionContextAllowsSubscriptionDetailsPayload(context) {
  if (!context) {
    return false;
  }
  const surface = context.surface;
  if (surface === 'paynow') {
    return false;
  }
  if (surface === 'portal') {
    return !!(context.hasSubscriptionContext && context.isSingleSubscriptionContext);
  }
  if (surface === 'checkout' || !surface) {
    return (
      isSubscriptionApmContextForNewPm(context) ||
      checkoutAllowSavedSubApmReturningCustomerMultiSubNewPurchase(context)
    );
  }
  return isSubscriptionApmContextForNewPm(context);
}

function lineItemsPolicyAllowsSend(context) {
  if (!context) {
    return false;
  }
  if (context.isPaynowMultiSubscriptionInvoiceSelection) {
    return true;
  }
  const surface = context.surface;
  if (surface === 'paynow') {
    return !!(context.lineItems && context.lineItems.length > 0);
  }
  if (surface === 'portal') {
    return !!(context.hasSubscriptionContext && context.isSingleSubscriptionContext);
  }
  if (surface === 'checkout' || !surface) {
    return (
      isSubscriptionApmContextForNewPm(context) ||
      checkoutAllowSavedSubApmReturningCustomerMultiSubNewPurchase(context)
    );
  }
  return isSubscriptionApmContextForNewPm(context);
}

/**
 * Whether a new sub-APM PM can be added in the current surface/context.
 * Portal blocks adding new sub-APM PMs (mandate is always against an existing subscription).
 * Paynow allows — it is a one-time collect flow.
 * Checkout allows when there is an unambiguous recurring subscription being purchased.
 */
export function canAddPm({ pm, context }) {
  let ret;
  if (!isSubscriptionBasedApm(pm)) {
    ret = true;
  } else {
    const surface = context && context.surface;
    if (surface === 'portal') {
      ret = false;
    } else if (surface === 'paynow') {
      ret = true;
    } else if (surface === 'checkout' || !surface) {
      ret = isSubscriptionApmContextForNewPm(context);
    } else {
      ret = isSubscriptionApmContextForNewPm(context);
    }
  }
  return ret;
}

/**
 * Whether a saved sub-APM PM can be shown for the current surface/context.
 * Paynow saved sub-APM is blocked here; Stripe Klarna paynow has its own stricter check
 * in `stripe-klarna-subscription-apm-policy.js` (single invoice + single subscription required).
 * Non-sub-APM saved PMs pass through unchanged.
 */
export function canShowExistingPm({ pm, context, savedPaymentSource = null }) {
  let ret;
  if (!isSubscriptionBasedApm(pm)) {
    ret = true;
  } else {
    const surface = context && context.surface;
    if (surface === 'paynow') {
      ret = false;
    } else if (surface === 'checkout' || !surface) {
      ret =
        isSubscriptionApmContextForNewPm(context) ||
        !!(context && context.hasSubscriptionContext && context.isSingleSubscriptionContext) ||
        checkoutAllowSavedSubApmReturningCustomerMultiSubNewPurchase(context);
    } else {
      ret = !!(context && context.hasSubscriptionContext && context.isSingleSubscriptionContext);
    }
  }
  return ret;
}

export function shouldSendSubscriptionDetails({ pm, context }) {
  const ret =
    isSubscriptionBasedApm(pm) && subscriptionContextAllowsSubscriptionDetailsPayload(context);
  return ret;
}

export function shouldSendLineItems({ pm, context }) {
  const ret =
    isSubscriptionBasedApm(pm) && hasSetLineItems(pm) && lineItemsPolicyAllowsSend(context);
  return ret;
}
